import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  DollarSign,
  BarChart3,
  MapPin,
  Calendar,
  ArrowUpRight,
  PieChart,
  Activity,
  Building2,
  Dice1,
  Dice6,
  LogIn,
  UserCheck,
  LogOut,
  Settings,
  ArrowRight,
  BookOpen,
  Sparkles,
} from "lucide-react";
import { useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";
import ProfileModal from "./components/ProfileModal";
import LegalModal from "./components/LegalModal";
import { getPosts, urlFor } from "./sanityClient";

function LandingPage({
  onViewDashboard,
  onViewBlog,
  onSelectBlogPost,
  onPreviewData,
  initialLegalTab = null,
}) {
  const { user, signOut } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(!!initialLegalTab);
  const [legalModalTab, setLegalModalTab] = useState(initialLegalTab || "terms");
  const [authMode, setAuthMode] = useState("signin");
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    if (initialLegalTab) {
      setLegalModalTab(initialLegalTab);
      setLegalModalOpen(true);
    }
  }, [initialLegalTab]);

  useEffect(() => {
    let isMounted = true;
    getPosts()
      .then((posts) => {
        if (isMounted && posts && posts.length > 0) {
          setRecentPosts(posts.slice(0, 3));
        }
      })
      .catch((err) => {
        console.error("Could not fetch recent posts for landing page:", err);
      });
    return () => {
      isMounted = false;
    };
  }, []);


  const openAuth = (mode = "signin") => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  };

  const handleDashboardClick = () => {
    if (user) {
      onViewDashboard();
    } else {
      openAuth("signin");
    }
  };

  const handleSignUpClick = () => {
    if (user) {
      onViewDashboard();
    } else {
      openAuth("signup");
    }
  };

  const features = [

    {
      icon: <BarChart3 className="h-8 w-8 text-blue-400" />,
      title: "Revenue Analytics",
      description:
        "Comprehensive analysis of casino revenue streams including slots, tables, and poker.",
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-green-400" />,
      title: "Performance Tracking",
      description:
        "Monitor year-over-year performance with detailed trend analysis and forecasting.",
    },
    {
      icon: <MapPin className="h-8 w-8 text-purple-400" />,
      title: "Location Insights",
      description:
        "Compare performance across different markets and geographical locations.",
    },
    {
      icon: <PieChart className="h-8 w-8 text-blue-400" />,
      title: "Market Share Analysis",
      description:
        "Understand market positioning and competitive landscape analysis.",
    },
    {
      icon: <Activity className="h-8 w-8 text-green-400" />,
      title: "Real-time Monitoring",
      description:
        "Live dashboard with real-time updates and performance indicators.",
    },
    {
      icon: <DollarSign className="h-8 w-8 text-purple-400" />,
      title: "Financial Reporting",
      description:
        "Detailed financial reports with customizable metrics and KPI tracking.",
    },
  ];

  const stats = [
    {
      label: "Casinos Tracked",
      value: "500+",
      icon: <Building2 className="h-5 w-5" />,
    },
    {
      label: "Revenue Analyzed",
      value: "$50B+",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Markets Covered",
      value: "25+",
      icon: <MapPin className="h-5 w-5" />,
    },
    {
      label: "Years of Data",
      value: "10+",
      icon: <Calendar className="h-5 w-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 w-full overflow-x-hidden">
      {/* Top Navigation Bar */}
      <nav className="border-b border-gray-800/80 bg-gray-900/60 backdrop-blur-md sticky top-0 z-40 w-full">
        <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8 h-18 flex items-center justify-between py-3.5 sm:py-4">
          <div className="flex items-center gap-2.5 sm:gap-6 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
              <div className="flex gap-1">
                <Dice1 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />
                <Dice6 className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-white tracking-tight">
                Casino<span className="text-white sm:text-transparent sm:bg-clip-text sm:bg-gradient-to-r sm:from-purple-300 sm:via-blue-300 sm:to-indigo-200">Figures</span>
              </span>
            </div>
            <button
              onClick={onViewBlog}
              className="text-xs sm:text-sm font-medium text-gray-200 hover:text-white transition-colors px-2.5 sm:px-3 py-1.5 rounded-xl hover:bg-gray-800/70 inline-flex items-center gap-1 shrink-0"
            >
              <span>Blog</span>
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {user ? (
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setProfileModalOpen(true)}
                  className="hidden sm:inline-flex items-center gap-2 text-xs text-gray-200 hover:text-white bg-gray-800/80 hover:bg-gray-800 border border-gray-700/60 hover:border-gray-600 px-3.5 py-1.5 rounded-xl transition-all group"
                  title="Account Settings"
                >
                  <UserCheck className="h-3.5 w-3.5 text-green-400 group-hover:text-green-300" />
                  <span>{user.user_metadata?.full_name || user.email}</span>
                  <Settings className="h-3 w-3 text-gray-400 group-hover:text-gray-200 ml-0.5" />
                </button>
                <button
                  onClick={onViewDashboard}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                >
                  Dashboard
                </button>
                <button
                  onClick={() => signOut()}
                  title="Sign Out"
                  className="p-1.5 sm:p-2 text-gray-300 hover:text-white rounded-xl hover:bg-gray-800 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => openAuth("signin")}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 text-gray-200 hover:text-white text-xs sm:text-sm font-medium transition-colors"
                >
                  <LogIn className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Sign In
                </button>
                <button
                  onClick={() => openAuth("signup")}
                  className="hidden sm:inline-flex px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>


      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/20 to-blue-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center">
            <div className="flex justify-center items-center gap-3 mb-6">
              <div className="flex gap-1">
                <Dice1
                  className="h-8 w-8 text-blue-400 animate-bounce"
                  style={{ animationDelay: "0s" }}
                />
                <Dice6
                  className="h-8 w-8 text-purple-400 animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                />
              </div>
              <h1 className="text-5xl font-bold text-white">
                Casino{" "}
                <span className="text-purple-300 sm:text-transparent sm:bg-clip-text sm:bg-gradient-to-r sm:from-purple-300 sm:via-blue-300 sm:to-indigo-200">
                  Figures
                </span>
              </h1>
            </div>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Comprehensive casino analytics platform providing deep insights
              into revenue performance, market trends, and operational metrics
              across the gaming industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <>
                  <button
                    onClick={handleDashboardClick}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 flex items-center gap-2 justify-center"
                  >
                    <Activity className="h-5 w-5" />
                    <span>Open Live Dashboard</span>
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={onViewBlog}
                    className="border-2 border-gray-700 text-gray-200 hover:text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800/50 hover:border-gray-600 transition-all duration-300"
                  >
                    Read Insights
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSignUpClick}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 flex items-center gap-2 justify-center"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={onPreviewData || handleDashboardClick}
                    className="border-2 border-gray-700 text-gray-200 hover:text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800/50 hover:border-gray-600 transition-all duration-300 flex items-center gap-2 justify-center"
                  >
                    <Activity className="h-4 w-4 text-purple-400" />
                    <span>Preview Data</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>


      {/* Stats Section */}
      <section className="py-16 bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center items-center gap-2 mb-2">
                  <div className="text-purple-400">{stat.icon}</div>
                  <div className="text-3xl font-bold text-white">
                    {stat.value}
                  </div>
                </div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Powerful Analytics{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-blue-300 to-indigo-200">
                Features
              </span>
            </h2>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Everything you need to analyze, understand, and optimize casino
              performance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gray-800/50 backdrop-blur-sm p-6 rounded-xl border border-gray-700 hover:border-gray-600 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-100 sm:text-gray-200 leading-relaxed text-sm sm:text-base">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Blog & Market Insights Section */}
      <section className="py-20 bg-gray-900/60 border-t border-gray-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold mb-3">
                <Sparkles className="h-3.5 w-3.5" />
                Industry Analysis & Intelligence
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                Latest Market{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-blue-300 to-indigo-200">
                  Insights
                </span>
              </h2>
              <p className="text-gray-200 mt-2 max-w-xl text-base">
                In-depth reports, quarterly revenue breakdowns, and strategic analysis across commercial gaming markets.
              </p>
            </div>
            <button
              onClick={onViewBlog}
              className="mt-4 md:mt-0 inline-flex items-center gap-2 text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors group"
            >
              <span>View all articles</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {recentPosts.length === 0 ? (
            <div className="rounded-2xl border border-gray-800/80 bg-gray-900/40 p-8 text-center max-w-md mx-auto">
              <BookOpen className="h-10 w-10 text-gray-600 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-white mb-1">
                Articles Coming Soon
              </h3>
              <p className="text-xs text-gray-300 mb-4">
                Our gaming analysts are publishing fresh reports. Explore our full research library.
              </p>
              <button
                onClick={onViewBlog}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-semibold transition-all hover:shadow-lg hover:shadow-purple-500/20"
              >
                Go to Blog Directory
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentPosts.map((post) => {
                const imageUrl = post.mainImage ? urlFor(post.mainImage)?.width(700)?.height(400)?.url() : null;
                const formattedDate = post.publishedAt
                  ? new Date(post.publishedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : null;

                return (
                  <article
                    key={post._id}
                    onClick={() => (onSelectBlogPost ? onSelectBlogPost(post.slug.current) : onViewBlog())}
                    className="group flex flex-col rounded-2xl bg-gray-900/70 border border-gray-800/80 hover:border-purple-500/40 hover:bg-gray-900 transition-all duration-300 overflow-hidden cursor-pointer shadow-lg hover:shadow-purple-500/5"
                  >
                    <div className="relative aspect-video w-full overflow-hidden bg-gray-800">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/20 to-blue-900/20">
                          <BookOpen className="h-8 w-8 text-gray-700" />
                        </div>
                      )}
                      {post.categories && post.categories[0] && (
                        <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-950/80 backdrop-blur-md text-purple-300 border border-gray-800">
                          {post.categories[0]}
                        </span>
                      )}
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                        {formattedDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formattedDate}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition-colors mb-2 line-clamp-2">
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-xs text-gray-300 line-clamp-2 mb-4 flex-1 leading-relaxed">
                          {post.excerpt}
                        </p>
                      )}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800/80 mt-auto">
                        <span className="text-xs text-gray-300">
                          {post.author?.name || "Analyst"}
                        </span>
                        <span className="text-xs font-semibold text-purple-400 group-hover:text-purple-300 inline-flex items-center gap-1 transition-colors">
                          Read Report
                          <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600/10 to-blue-600/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Casino Analytics?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Join hundreds of casino operators who trust Casino Figures for their
            data insights
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <>
                <button
                  onClick={handleDashboardClick}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300"
                >
                  Open Live Dashboard
                </button>
                <button
                  onClick={onViewBlog}
                  className="border-2 border-gray-700 text-gray-200 hover:text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800/50 hover:border-gray-600 transition-all duration-300"
                >
                  Read Insights
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSignUpClick}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span>Get Started</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={onPreviewData || handleDashboardClick}
                  className="border-2 border-gray-700 text-gray-200 hover:text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800/50 hover:border-gray-600 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <Activity className="h-4 w-4 text-purple-400" />
                  <span>Preview Data</span>
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        initialMode={authMode}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => onViewDashboard()}
      />

      {/* Profile Modal */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
      />



      {/* Footer */}
      <footer className="py-12 border-t border-gray-800 bg-gray-950/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <div className="flex items-center gap-2">
              <Dice1 className="h-6 w-6 text-blue-400" />
              <Dice6 className="h-6 w-6 text-purple-400" />
              <span className="text-white font-semibold text-lg">
                Casino Figures
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-300">
              <button
                onClick={onViewBlog}
                className="hover:text-purple-400 transition-colors"
              >
                Blog
              </button>
              <button
                onClick={handleDashboardClick}
                className="hover:text-purple-400 transition-colors"
              >
                Dashboard
              </button>
              <button
                onClick={() => {
                  setLegalModalTab("terms");
                  setLegalModalOpen(true);
                }}
                className="hover:text-purple-400 transition-colors"
              >
                Terms & Conditions
              </button>
              <button
                onClick={() => {
                  setLegalModalTab("privacy");
                  setLegalModalOpen(true);
                }}
                className="hover:text-blue-400 transition-colors"
              >
                Privacy Policy
              </button>
              <button
                onClick={() => {
                  setLegalModalTab("responsible");
                  setLegalModalOpen(true);
                }}
                className="text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"
              >
                <span>Responsible Gaming</span>
                <span className="px-1.5 py-0.2 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded">
                  21+
                </span>
              </button>
              <button
                onClick={() => {
                  setLegalModalTab("disclaimer");
                  setLegalModalOpen(true);
                }}
                className="hover:text-amber-400 transition-colors"
              >
                Data Disclaimer
              </button>
            </div>
          </div>

          <div className="text-center text-xs text-gray-400">
            © 2026 Casino Figures. All rights reserved. Historical RTP and casino hold figures are compiled from public regulatory filings and do not guarantee future outcomes.
          </div>
        </div>
      </footer>

      {/* Legal Modal */}
      <LegalModal
        isOpen={legalModalOpen}
        initialTab={legalModalTab}
        onClose={() => setLegalModalOpen(false)}
      />
    </div>
  );
}

export default LandingPage;
